/** @typedef {{ el: Node, panelEl: Node, active: boolean, disabled: boolean }} TabItem */

import scssVars from '!!style-loader!css-loader!sass-loader!@/sass/_base.scss';
import keyCodes from '@/utils/key-codes';
import { reflow } from 'bootstrap-vue/src/utils/dom';
import parseTransitionTime from '@/utils/transition-time';

const SELECTOR = 'vjs-tabs';
const LINK_SELECTOR = 'vjs-tab-link';

const DEFAULT_SHOWN_CLASS = '-active';
const DEFAULT_DISABLED_CLASS = '-disabled';

class Tabs {
    constructor(el) {
        this.el = el;
        this.id = this.el.getAttribute(SELECTOR);
        this.idAttr = this.el.id;

        this.transitioning = false;
        this.transitionTime = parseTransitionTime(scssVars['panel-transitionTime']) || 300;
        this.activeTab = 0;

        this.tabLinkShownClass = this.el.dataset.linkShownClass || DEFAULT_SHOWN_CLASS;
        this.tabLinkDisabledClass = this.el.dataset.linkDisabledClass || DEFAULT_DISABLED_CLASS;
        this.panelHiddenClass = scssVars['panel-hiddenClass'] || 'collapse';
        this.panelTransitioningClass = scssVars['panel-transitioningClass'] || 'collapsing';
        this.panelShownClass = scssVars['panel-shownClass'] || 'show';

        /** @type {Array<TabItem>} */
        this.tabs =
            [ ...this.el.querySelectorAll(`[${LINK_SELECTOR}]`) ] // array spread to convert nodelist into array
                .map(el => {
                    return {
                        el,
                        id: el.dataset.target.replace('#', ''),
                        panelEl: document.getElementById(el.dataset.target),
                        active: false,
                        disabled: el.dataset.disabled?.toLowerCase() === 'true'
                    };
                }) // map array to an object containing both the tab link and panel elements
                .filter(({ el, panelEl }) => !this.id || (panelEl && el.getAttribute(LINK_SELECTOR) === this.id)); // filter out any tabs that don't match instance id or can't resolve its panel target

        if(this.tabs.length > 0) {
            this.init();
        }
    }

    init() {
        this.setupInitialActive();
        this.setupTabs();
        this.setupBindedListeners();
        this.setupListeners();
    }

    destroy() {
        this.destroyListeners();
    }

    setupBindedListeners() {
        this.tabs.forEach((el, i) => {
            this['bindedToggle' + i] = this.toggleTabs.bind(this, i);
        });
    }

    setupListeners() {
        this.tabs.forEach((tab, i) => {
            tab.el.addEventListener('click', this['bindedToggle' + i]);
            tab.el.addEventListener('keydown', this.bindedOnKeydown);
        });
    }

    destroyListeners() {
        this.tabs.forEach((tab, i) => {
            tab.el.removeEventListener('click', this['bindedToggle' + i]);
            tab.el.removeEventListener('keydown', this.bindedOnKeydown);
        });
    }

    setupInitialActive() {
        this.hasActive = false;
        for (let i = 0; i < this.tabs.length; i++) {
            if(this.tabs[i].el.classList.contains(this.tabLinkShownClass)) {
                this.hasActive = true;
                this.tabs[i].active = true;
                this.activeTab = i;
            }
        }

        if(!this.hasActive) {
            this.tabs[0].active = true;
        }
    }

    setupTabs() {
        this.tabs.forEach((tab, i) => {
            // setup tab link ids
            const tabLinkId = `${this.id}__tabLink__${tab.panelEl.id}`;
            tab.el.id = tabLinkId;

            // setup roles
            tab.el.setAttribute('role', 'tab');
            tab.panelEl.setAttribute('role', 'tabpanel');

            // setup aria
            tab.el.setAttribute('aria-setsize', this.tabs.length);
            tab.el.setAttribute('aria-posinset', i + 1);
            tab.el.setAttribute('aria-controls', tab.panelEl.id);
            tab.panelEl.setAttribute('aria-labelledby', tabLinkId);

            if(tab.active) {
                tab.el.classList.add(this.tabLinkShownClass);
                tab.el.setAttribute('aria-selected', 'true');

                tab.panelEl.classList.add(this.panelShownClass);
                tab.panelEl.setAttribute('aria-hidden', 'false');
            } else {
                tab.el.classList.remove(this.tabLinkShownClass);
                tab.el.setAttribute('aria-selected', 'false');
                tab.el.setAttribute('tabindex', '-1');

                tab.panelEl.classList.remove(this.panelShownClass);
                tab.panelEl.setAttribute('aria-hidden', 'true');

                this.toggleDisabled(tab);
            }
        });
    }

    transition(prev, next) {
        this.transitioning = true;

        next.panelEl.style.opacity = 0;

        next.panelEl.classList.add(this.panelShownClass);
        next.panelEl.classList.remove(this.panelHiddenClass);

        prev.panelEl.classList.add(this.panelTransitioningClass);
        next.panelEl.classList.add(this.panelTransitioningClass);

        const prevHeight = prev.panelEl.clientHeight;
        const nextHeight = next.panelEl.scrollHeight;

        prev.panelEl.style.height = `${prevHeight}px`;
        next.panelEl.style.height = `${prevHeight}px`;

        reflow(prev.panelEl);
        reflow(next.panelEl);

        prev.panelEl.style.height = `${nextHeight}px`;
        next.panelEl.style.height = `${nextHeight}px`;

        prev.panelEl.style.opacity = 0;
        next.panelEl.style.position = 'absolute';
        next.panelEl.style.top = '0';

        setTimeout(() => {
            next.panelEl.style.opacity = 1;
        }, this.transitionTime);

        setTimeout(() => {
            prev.panelEl.classList.remove(this.panelShownClass);
            prev.panelEl.classList.add(this.panelHiddenClass);

            prev.panelEl.classList.remove(this.panelTransitioningClass);
            next.panelEl.classList.remove(this.panelTransitioningClass);

            prev.panelEl.removeAttribute('style');
            next.panelEl.removeAttribute('style');

            this.transitioning = false;
        }, this.transitionTime * 2);
    }

    onKeydown(evt) {
        const next = evt.which === keyCodes.RIGHT || evt.which === keyCodes.DOWN;
        const prev = evt.which === keyCodes.UP || evt.which === keyCodes.LEFT;

        if(next || prev) {
            evt.preventDefault();
            evt.stopPropagation();

            if(!this.transitioning) {
                this.activateNextEnabled(next);
            }
        }
    }

    activateNextEnabled(next, index = this.activeTab) {
        const nextTabIndex = next ? index + 1 : index - 1;
        if(nextTabIndex > -1 && nextTabIndex < this.tabs.length) {
            if(this.tabs[nextTabIndex].disabled) {
                this.activateNextEnabled(next, nextTabIndex);
            } else {
                this.toggleTabs(nextTabIndex);
                this.tabs[nextTabIndex].el.focus();
            }
        }
    }

    toggleTabs(tabIndex, evt) {
        evt?.preventDefault();

        if(tabIndex !== this.activeTab && !this.tabs[tabIndex].disabled && !this.transitioning) {
            this.toggle(this.tabs[this.activeTab]);
            this.toggle(this.tabs[tabIndex]);

            this.transition(this.tabs[this.activeTab], this.tabs[tabIndex]);

            this.activeTab = tabIndex;
        }
    }

    toggleDisabled(tab) {
        tab.el.setAttribute('aria-disabled', tab.disabled);
        if(tab.disabled) {
            tab.el.classList.add(this.tabLinkDisabledClass);
        } else {
            tab.el.classList.remove(this.tabLinkDisabledClass);
        }
    }

    toggle(tab) {
        tab.active = !tab.active;

        // set aria
        tab.el.setAttribute('aria-selected', `${tab.active}`);
        tab.panelEl.setAttribute('aria-hidden', `${!tab.active}`);

        // set tabindex
        if(tab.active) {
            tab.el.removeAttribute('tabindex');
        } else {
            tab.el.setAttribute('tabindex', '-1');
        }

        // set classes
        tab.el.classList.toggle(this.tabLinkShownClass);
    }

    bindedOnKeydown = this.onKeydown.bind(this);
}

let instanceArr = [];

export const utils = {
    $next(id) {
        instanceArr.forEach(instance => {
            if(instance.idAttr === id) {
                instance.activateNextEnabled(true);
            }
        });
    },
    $prev(id) {
        instanceArr.forEach(instance => {
            if(instance.idAttr === id) {
                instance.activateNextEnabled(false);
            }
        });
    },
    $enableTab(id, tabId) {
        instanceArr.forEach(instance => {
            if(instance.idAttr === id) {
                const tab = instance.tabs.find(tab => tab.id === tabId);
                if(tab) {
                    tab.disabled = false;
                    instance.toggleDisabled(tab);
                }
            }
        });
    },
    $disableTab(id, tabId) {
        instanceArr.forEach(instance => {
            if(instance.idAttr === id) {
                const tab = instance.tabs.find(tab => tab.id === tabId);
                if(tab) {
                    if(tab.active) {
                        instance.activateNextEnabled(true);
                    }

                    tab.disabled = true;
                    instance.toggleDisabled(tab);
                }
            }
        });
    }
};

export function destroy() {
    instanceArr.forEach(instance => instance?.destroy());
    instanceArr.splice(0, instanceArr.length);
}

export default function() {
    const elements = document.querySelectorAll(`[${SELECTOR}]`);
    elements?.forEach((element, i) => {
        instanceArr[i] = new Tabs(element);
    });
}
